import React, { useState, useEffect } from 'react';
import { Star, Mail, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import gmailService from '../services/gmailService';
import toast from 'react-hot-toast';

/**
 * Google Review Request Component
 * Final step in estate planning workflow to request client reviews
 */
const GoogleReviewRequest = ({ client, onComplete }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState(null);

  // Google API credentials (these should be in environment variables)
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';
  const GOOGLE_REVIEW_LINK = 'https://g.page/r/Cb7wtAyWrUCTEBM/review';

  useEffect(() => {
    initializeGoogleAPI();
  }, []);

  /**
   * Initialize Gmail API
   */
  const initializeGoogleAPI = async () => {
    try {
      if (!GOOGLE_CLIENT_ID || !GOOGLE_API_KEY) {
        setError('Google API credentials not configured. Please add REACT_APP_GOOGLE_CLIENT_ID and REACT_APP_GOOGLE_API_KEY to your environment variables.');
        return;
      }

      await gmailService.initializeGoogleAPI(GOOGLE_CLIENT_ID, GOOGLE_API_KEY);
      setIsInitialized(true);
      setError(null);
    } catch (err) {
      console.error('Failed to initialize Gmail API:', err);
      setError('Failed to initialize Gmail API. Please check your Google API configuration.');
    }
  };

  /**
   * Handle sending review request
   */
  const handleSendReviewRequest = async () => {
    if (!client?.email) {
      toast.error('Client email not found');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const result = await gmailService.createReviewRequestDraft(
        client.email,
        client.name,
        GOOGLE_REVIEW_LINK
      );

      if (result.success) {
        setIsCompleted(true);
        toast.success('Review request draft created in Gmail! You can now review and send it.');

        // Mark this workflow step as complete
        if (onComplete) {
          onComplete();
        }

        // Open Gmail in new tab to show the draft
        window.open('https://mail.google.com/mail/u/0/#drafts', '_blank');
      } else {
        throw new Error(result.error || 'Failed to create draft');
      }
    } catch (err) {
      console.error('Error creating review request:', err);
      setError(err.message || 'Failed to create review request draft');
      toast.error('Failed to create review request. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="google-review-request">
      <div className="workflow-step-card">
        <div className="step-header">
          <div className="step-icon">
            <Star size={24} className={isCompleted ? 'text-green-600' : 'text-blue-600'} />
          </div>
          <div className="step-title">
            <h3>Request Google Review</h3>
            <p className="step-description">
              Send a professional review request to the client via Gmail
            </p>
          </div>
          {isCompleted && (
            <div className="step-status">
              <CheckCircle size={24} className="text-green-600" />
              <span className="status-text">Completed</span>
            </div>
          )}
        </div>

        <div className="step-content">
          <div className="info-box">
            <div className="info-item">
              <strong>Client:</strong>
              <span>{client?.name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <strong>Email:</strong>
              <span>{client?.email || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <strong>Review Link:</strong>
              <a
                href={GOOGLE_REVIEW_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="review-link"
              >
                {GOOGLE_REVIEW_LINK}
              </a>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {!error && !isInitialized && (
            <div className="warning-message">
              <Loader size={18} className="animate-spin" />
              <span>Initializing Gmail API...</span>
            </div>
          )}

          {isCompleted && (
            <div className="success-message">
              <CheckCircle size={18} />
              <div>
                <strong>Draft created successfully!</strong>
                <p>The review request email draft is now in your Gmail account. You can review, edit, and send it from Gmail.</p>
              </div>
            </div>
          )}

          <div className="step-actions">
            <button
              className={`btn-primary ${isCompleted ? 'btn-success' : ''}`}
              onClick={handleSendReviewRequest}
              disabled={isSending || !isInitialized || !client?.email}
            >
              {isSending ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  Creating Draft...
                </>
              ) : isCompleted ? (
                <>
                  <CheckCircle size={20} />
                  Draft Created
                </>
              ) : (
                <>
                  <Mail size={20} />
                  Send Review Request
                </>
              )}
            </button>

            {isCompleted && (
              <button
                className="btn-secondary"
                onClick={() => window.open('https://mail.google.com/mail/u/0/#drafts', '_blank')}
              >
                Open Gmail Drafts
              </button>
            )}
          </div>

          <div className="help-text">
            <p>
              <strong>How it works:</strong>
            </p>
            <ol>
              <li>Click "Send Review Request" to create a draft email in your Gmail account</li>
              <li>The draft will contain a professional review request with your Google review link</li>
              <li>Review and edit the draft in Gmail if needed</li>
              <li>Send the email directly from Gmail</li>
            </ol>
          </div>
        </div>
      </div>

      <style>{`
        .google-review-request {
          margin-top: 20px;
        }

        .workflow-step-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .step-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .step-icon {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          background: #eff6ff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .step-title {
          flex: 1;
        }

        .step-title h3 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
          color: #111827;
        }

        .step-description {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .step-status {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #059669;
        }

        .status-text {
          font-weight: 600;
          font-size: 14px;
        }

        .step-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .info-box {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .info-item {
          display: flex;
          gap: 12px;
        }

        .info-item strong {
          min-width: 100px;
          color: #374151;
        }

        .info-item span {
          color: #6b7280;
        }

        .review-link {
          color: #2563eb;
          text-decoration: none;
        }

        .review-link:hover {
          text-decoration: underline;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #991b1b;
          font-size: 14px;
        }

        .warning-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          color: #92400e;
          font-size: 14px;
        }

        .success-message {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          color: #166534;
        }

        .success-message strong {
          display: block;
          margin-bottom: 4px;
        }

        .success-message p {
          margin: 0;
          font-size: 14px;
          color: #15803d;
        }

        .step-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-primary,
        .btn-secondary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #2563eb;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #1d4ed8;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2);
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-primary.btn-success {
          background: #059669;
        }

        .btn-primary.btn-success:hover:not(:disabled) {
          background: #047857;
        }

        .btn-secondary {
          background: white;
          color: #374151;
          border: 2px solid #e5e7eb;
        }

        .btn-secondary:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }

        .help-text {
          background: #f9fafb;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 4px;
        }

        .help-text p {
          margin: 0 0 12px 0;
          font-size: 14px;
          color: #374151;
        }

        .help-text ol {
          margin: 0;
          padding-left: 20px;
          color: #6b7280;
          font-size: 14px;
        }

        .help-text li {
          margin-bottom: 8px;
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default GoogleReviewRequest;
